if (!customElements.get('seat-total')) {
  customElements.define(
    'seat-total',
    class SeatTotal extends HTMLElement {
      connectedCallback() {
        this.quantityInput = document.getElementById(this.dataset.quantityInput);
        this.productForm = document.getElementById(this.dataset.productForm);
        this.productInfo = this.closest('product-info');
        this.amountElement = this.querySelector('[data-seat-total-amount]');
        this.countElement = this.querySelector('[data-seat-total-count]');
        this.recurringElement = this.querySelector('[data-seat-total-recurring]');

        if (!this.quantityInput || !this.productForm || !this.amountElement) return;

        this.pricingData = this.readPricingData();

        this.update = this.update.bind(this);
        this.quantityInput.addEventListener('input', this.update);
        this.quantityInput.addEventListener('change', this.update);
        this.productInfo?.addEventListener('change', this.update);

        this.watchSelection();
        this.update();
      }

      disconnectedCallback() {
        this.quantityInput?.removeEventListener('input', this.update);
        this.quantityInput?.removeEventListener('change', this.update);
        this.productInfo?.removeEventListener('change', this.update);
        clearInterval(this.selectionTimer);
      }

      readPricingData() {
        const script = this.querySelector('[data-seat-total-data]');
        if (!script) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.warn('Unable to read subscription pricing data', error);
          return null;
        }
      }

      // The subscription app writes [name="selling_plan"] only at add to cart time, and it mutates
      // its own controls by assigning to value rather than dispatching events, so there is nothing
      // for a listener or a MutationObserver to catch. Poll the selection instead.
      watchSelection() {
        this.lastSelection = this.currentSelection();
        this.selectionTimer = setInterval(() => {
          const selection = this.currentSelection();
          if (selection === this.lastSelection) return;
          this.lastSelection = selection;
          this.update();
        }, 200);
      }

      currentSelection() {
        return `${this.selectedVariantId()}:${this.selectedPlanId()}`;
      }

      selectedVariantId() {
        return String(this.productForm.querySelector('[name="id"]')?.value || '');
      }

      // Until the app fills in the real field, the live choice lives in its own widget: a
      // subs_type_* radio picks one time versus subscription, and a subs_interval select carries
      // the selling plan id.
      selectedPlanId() {
        const sellingPlanId = this.productForm.querySelector('[name="selling_plan"]')?.value;
        if (sellingPlanId) return String(sellingPlanId);

        const purchaseType = this.productForm.querySelector('[name^="subs_type_"]:checked');
        if (purchaseType && purchaseType.dataset.type !== 'subscription') return '';

        return String(this.productForm.querySelector('[name="subs_interval"]')?.value || '');
      }

      getSelectedPricing() {
        const fallbackPrice = Number(this.dataset.unitPrice) || 0;
        const variantId = this.selectedVariantId();
        const sellingPlanId = this.selectedPlanId();
        const variant = this.pricingData?.variants?.[variantId];
        const allocation = variant?.allocations?.[sellingPlanId];

        if (!allocation) {
          return { current: variant?.price ?? fallbackPrice, renewal: null, interval: '' };
        }

        return {
          current: allocation.price,
          renewal: allocation.recurring_price ?? allocation.price,
          interval: this.pricingData?.plans?.[sellingPlanId]?.interval || '',
        };
      }

      update() {
        const quantity = Math.max(1, Number.parseInt(this.quantityInput.value, 10) || 1);
        const pricing = this.getSelectedPricing();

        this.countElement.textContent = `${quantity} ${quantity === 1 ? 'seat' : 'seats'}`;
        this.amountElement.textContent = this.formatMoney(pricing.current * quantity);

        if (pricing.renewal === null || !pricing.interval) {
          this.recurringElement.hidden = true;
          this.recurringElement.textContent = '';
          return;
        }

        const renewalLead = pricing.renewal === pricing.current ? 'Billed at' : 'Then';
        const renewalCadence = pricing.interval === 'month' ? 'monthly' : 'annually';
        this.recurringElement.textContent = `${renewalLead} ${this.formatMoney(
          pricing.renewal * quantity,
        )} per ${pricing.interval} for ${quantity} ${quantity === 1 ? 'seat' : 'seats'}. Renews ${renewalCadence} until canceled.`;
        this.recurringElement.hidden = false;
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        }).format(cents / 100);
      }
    },
  );
}
